import { PickWithOptionality } from '@thyme/libs/src/typing/partial'
import { Person } from '@/api/entities/people/types'
import { Nullable } from '@/libs/typing/base'
import { DateTimeUTC } from '@/libs/typing/timestamps'
import { Turn } from '../communications/texting'

// TYPES --------------------
export type Digit = { digit: string; help?: string; secondary?: boolean }

export type PhoneNumberNameAndType = {
  name: string
  type: string
}

export type PhoneNumberNameMap = {
  [key: string]: PhoneNumberNameAndType[]
}

export type PhoneNumber = {
  countryCode: string
  messagingOptInStatus: boolean | null
  phoneNumber: string
  phoneNumberId: string
  extension?: string | null
  person?: Person
  reachabilityStatus?: PhoneNumberReachabilityStatus | null
  type?: PhoneNumberType | null
}

export type CommunicationConsent = {
  communicationConsentId: string
  mapEntityPhoneNumberId: string
  channel: CommunicationConsentChannel
  category?: CommunicationConsentCategory | null
  consentSource?: CommunicationConsentSource | null
  consentMetadata?: string | null
  isConsented?: boolean
}

export type MapEntityPhoneNumber = {
  mapEntityPhoneNumberId: string
  entityId: string
  phoneNumberId: string
  isPrimary: boolean
  note?: string
  source?: string
  validity?: MapEntityPhoneNumberValidity | null
  phoneNumber: PhoneNumber
  communicationConsents?: CommunicationConsent[]
}

export type MapEntityPhoneNumberPersonSms = {
  person: Person

  relationshipToPatient?: Nullable<string>

  communicationId?: string
  twilioConversationIds?: string[]
  turn?: Turn
  updatedAt?: DateTimeUTC
  people?: Person[]

  lastUpdateDate?: DateTimeUTC
  lastTextMessage?: string
  lastTextMessageAuthor?: string | null
} & MapEntityPhoneNumber

export type PatientPhoneNumberForm = {
  phoneNumber: string
  isPrimary: boolean
  isWrongNumber: boolean
  messagingOptInStatus: boolean | null
  reachabilityStatus: PhoneNumberReachabilityStatus | null
  countryCode: string
  note: string
}

export type CreatePhoneNumberRequest = PickWithOptionality<
  PhoneNumber,
  (typeof createPhoneNumberRequiredFields)[number],
  (typeof createPhoneNumberOptionalFields)[number]
>

export type CreateEntityPhoneNumberRequest = {
  phoneNumber: CreatePhoneNumberRequest
  isPrimary: boolean | null
  note: string | null
}

export type UpsertCommunicationConsentRequest = {
  mapEntityPhoneNumberId: string | null
  channel: CommunicationConsentChannel
  consentSource: CommunicationConsentSource
  category: CommunicationConsentCategory
  isConsented: boolean
}

export type CreateEntityPhoneNumberResponse = MapEntityPhoneNumber

export type UpsertMapEntityPhoneNumberResponse = MapEntityPhoneNumber & {
  /**
   * Whether texts were successfully migrated from the unknown texts patient
   * to the actual member. Defaults to true on the BE, including when there was
   * no migration to run. The phone number map is saved first, then the
   * migration runs. If migration fails (`false`), the map is still saved
   * but texts remain on the unknown texts patient and its map is kept
   * intact to avoid losing them.
   */
  unknownTextsMigrationSucceeded: boolean
}

// INTERFACES --------------------
export interface DialOptions {
  phoneNumber: string
  outboundNumber?: PhoneNumber | null
  outboundQueueArn?: string | null
}

// ENUMS --------------------
// Matches the backend MapEntityPhoneNumberValidity enum.
// Validity lives on the entity-phone mapping, not the phone number itself.
export enum MapEntityPhoneNumberValidity {
  WRONG_NUMBER = 'WRONG_NUMBER',
}

// Matches the backend PhoneNumberReachabilityStatus enum.
// Surfaced in the UI as "Operability Status" per product requirements.
export enum PhoneNumberReachabilityStatus {
  OPERABLE = 'OPERABLE',
  INOPERABLE = 'INOPERABLE',
}

export enum PhoneNumberType {
  LANDLINE = 'landline',
  MOBILE = 'mobile',
  VOIP = 'voip',
  OTHER = 'other',
}

export enum PhoneNumberSource {
  NON_LEXIS_NEXIS_LEGACY_SOURCE = 'NON_LEXIS_NEXIS_LEGACY_SOURCE',
  LEGACY_SOURCE = 'LEGACY_SOURCE',
  LEXIS_NEXIS = 'LEXIS_NEXIS',
  ELECTRONIC_HEALTH_RECORD = 'ELECTRONIC_HEALTH_RECORD',
  ZUS = 'ZUS',
  MEMBER_PROVIDED = 'MEMBER_PROVIDED',
  PARTNER_PROVIDED = 'PARTNER_PROVIDED',
  USER_PROVIDED = 'USER_PROVIDED',
}

// Communication Consent Enums
export enum CommunicationConsentChannel {
  CALL = 'call',
  SMS = 'sms',
  EMAIL = 'email',
  DIRECT_MAIL = 'direct_mail',
}

export enum CommunicationConsentCategory {
  TRANSACTIONAL = 'transactional',
  PROMOTIONAL = 'promotional',
}

export enum CommunicationConsentSource {
  KEYWORD = 'keyword',
  MANUAL = 'manual',
  INHERITED = 'inherited',
  LEGACY = 'legacy',
  UPSERTED = 'upserted',
}

// CONSTANTS --------------------
export const CURRENT_MEMBER_STR = 'Current member'

export const createPhoneNumberRequiredFields = [
  'countryCode',
  'phoneNumber',
] as const

export const createPhoneNumberOptionalFields = [
  'messagingOptInStatus',
  'reachabilityStatus',
] as const

export const digits: Digit[] = [
  { digit: '1' },
  { digit: '2', help: 'abc' },
  { digit: '3', help: 'def' },
  { digit: '4', help: 'ghi' },
  { digit: '5', help: 'jkl' },
  { digit: '6', help: 'mno' },
  { digit: '7', help: 'pqrs' },
  { digit: '8', help: 'tuv' },
  { digit: '9', help: 'wxyz' },
  { digit: '#', secondary: true },
  { digit: '0' },
  { digit: '*', secondary: true },
]
